import React, { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import Link from '../../base_link/component';
import Typeset from '@components/base_typeset/component';
import Text from '@components/ui_text/component';
import { numFormatToCurrency } from '@utils';
import Icon from '../../base_svg-icon/component';
import Dropdown from '../../ui_dropdown/component';
import './styles/styles.scss';


/**
 * Product other editions component
 *
 * This wil render all the alternatives for a product.
 * i.e. There is a paperback book and there is a hardcover as alternative, and also a DVD
 *
 */
class ProductOtherEditions extends Component {
  constructor(props) {
    super(props);
    this.state = {
      open: false,
    };
    this.dropDownRef = createRef();
  }

  componentDidMount() {
    window.addEventListener('click',  this.handleClickOutside);
  }

  componentWillUnmount() {
    window.removeEventListener('click', this.handleClickOutside);
  }

  handleClickOutside = (e) => {
    if (this.dropDownRef && !this.dropDownRef?.current?.contains(e.target)) {
      this.setState({ open: false });
    }
  };

  get items() {
    const { items = [] } = this.props.data || {};
    // if one item, then set it to active
    if (items.length === 1) {
      items[0].active = true;
    }

    return items.sort(this.determinePosition);
  }

  /**
   * Sorting function for the fixed order of other editions
   *
   * @param {string} a
   * @param {string} b
   * @return {number}
   */
  determinePosition = (a, b) => {
    // Initially sort by type (desc)
    if (a.type?.toUpperCase() < b.type?.toUpperCase()) {
      return 1;
    } else if (a.type?.toUpperCase() > b.type?.toUpperCase()) {
      return -1;
    }
    const priceA = a.price ? parseFloat(a.price, 10) : 0;
    const priceB = b.price ? parseFloat(b.price, 10) : 0;
    // if equal, sort by price (asc)
    if (priceA < priceB) {
      return -1;
    } else if (priceA > priceB) {
      return 1;
    }
    return 0;
  };

  getPrice(price) {
    // if there is only one item, don't show the price
    if (this.items.length === 1) {
      return null;
    }

    if (typeof price === 'string') {
      return numFormatToCurrency(price);
    }
    if (typeof price === 'object') {
      const priceObjects = [];
      price.regular && priceObjects.push(<div className="content-klant">{numFormatToCurrency(price.regular)}</div>);
      return priceObjects;
    }
    return null;
  }

  // Closes the modal if the component is rendered inside one (e.g., in the eBook modal).
  closeModal = () => {
      this.props.store.modal?.closeModal();
  }

  getElements(mobile = false) {
    /*
     * hide the main elements on xs and sm view
     * exept when there is only one item
     * and exept for the mobile content
     */
    const liClasses = (mobile || this.items.length === 1) ? '' : 'hidden-xs hidden-sm';

    return (
      this.items.filter(item => (mobile ? !item.active : true))
        .map((item, i) => {
          item.version = item.type || item.version;
          return (
            <li className={`${liClasses} ${item.active ? 'active' : ''}`} key={i} onClick={this.closeModal}>
              <Link to={item.url}>
                <label>
                  <div>
                    <span className="head">
                      {item.version && (
                        <span className="version">{item.version}</span>
                      )}
                      {item.price && (
                        <span className="price">{this.getPrice(item.price)}</span>
                      )}
                    </span>
                    {item.action && (
                      <span className="action"><strong>{item.action}</strong></span>
                    )}
                    {item.na && (
                      <span className="na">{item.na}</span>
                    )}
                  </div>

                  {item.format && (
                    <div className="format">{item.format}</div>
                  )}
                </label>
              </Link>
            </li>
          );
        })
    );
  }

  get activeElement() {
    const item = this.items.find(item => !!item.active);
    if (!item) {
      return null;
    }
    return (
      <>
        <div>
          {item.version && (
            <span className="version">{item.version}</span>
          )}
          {item.price && (
            <span className="price pull-right">
              <span className="price pull-right">{this.getPrice(item.price)}</span>
            </span>
          )}

          <Icon
            name={`ico-arrow-${!!this.state.open ? 'up' : 'down'}`}
          />

        </div>
        {item.format && (
          <div className="format">{item.format}</div>
        )}
      </>
    );
  }

  render() {
    return (
      <div className="product-detail-editions radio" id="product-othereditions">
        <Typeset tag="div" bold><Text>pdp.editions.choose</Text></Typeset>
        <ul className="list-inline list-unstyled">
          {this.getElements()}
          {this.items.length > 1 && (
            <li className="menu-dropdown visible-xs visible-sm">
              <Dropdown
                label={this.activeElement}>
                {this.getElements(true)}
              </Dropdown>
            </li>
          )}
        </ul>
      </div>
    );
  }
}

ProductOtherEditions.propTypes = {
  data: PropTypes.shape({
    /**
     * All data is in the items.
     * The Items are an array of the book alternatives
     */
    items: PropTypes.arrayOf(
      /**
       * The array consists of objects which may have some values
       */
      PropTypes.shape({
        /**
         * The active state tells the item to be active
         */
        active: PropTypes.bool,
        /**
         * Version is for what kind of alternative it is, like 'Paperag'
         */
        type: PropTypes.string,
        /**
         * The price can be a string with the price of the product like 4,99
         * Or it can be an object with the regular and the member price.
         * ```
         * price: '4,99'
         * OR
         * price: {
         *      regular:'4,99',
         *      member:'3,99'
         * }
         *
         * ```
         */
        price: PropTypes.string,
        /**
         * The link to the product
         */
        url: PropTypes.string,
        /**
         * The sales action description like 'DAGDEAL'
         */
        action: PropTypes.string,
        /**
         * Small text to explain somthing like 'niet voorradig'
         */
        na: PropTypes.string,
      })
    ).isRequired,
  }),
  onChange: PropTypes.func,
};


export default ProductOtherEditions;
